// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input, NgZone,
  OnInit,
  Output, ViewChild,
  ViewContainerRef
} from '@angular/core';

import { AppConstants, LMTransformOptions } from '@utils/app.constants';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMFormPersonalization } from '@states/models/lm-form-personalization.model';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMContextService, LMLoggerService } from '@services/index';
import { LMModel } from '@form/model/lm-model';
import { LMBrowserUtilService, LMCommonUtilService } from '@utils/index';
import { LMFormContentFormComponent } from './lm-form-content-form.component';
import { LMFieldUtilService } from '@services/utils';
import { trim } from 'lodash-es';
import { LMFormSpecUtilService } from './services';

@Component({
  selector:    'lm-form-content-search',
  templateUrl: 'lm-form-content-search.component.html',
  styleUrls:   [ 'lm-form-content-search.component.scss' ]
})

export class LMFormContentSearchComponent implements AfterViewInit, OnInit {

  @Input() formSpec: LMFormSpec = null;
  @Input() formOptions: LMFormOptions = null;

  @Input() specFields: LMSpecFields;
  @Input() layout: LMFormLayout;
  @Input() model: LMModel;
  @Input() formState: LMFormState;
  @Input() placeHolder: ViewContainerRef;
  @Input() formPersonalization: LMFormPersonalization;
  @Input() columnarText: string;

  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  @Output() searchButtonClicked = new EventEmitter<LMFilterEvent>();
  @Output() clearSearchButtonClicked = new EventEmitter<LMFilterEvent>();

  @HostListener('window:resize') onResize() {
    this.setMaxHeight();
  }

  // Stopping propagation of the enter key event so that a select modal won't close when using a
  // search form to filter a list on a select
  @HostListener('keypress', [ '$event' ]) onKeyPress(event) {
    if (event.which === 13 && event.srcElement.nodeName !== 'BUTTON') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  @ViewChild('LMFormContentForm') lmFormContentForm: LMFormContentFormComponent;

  public searchTitle: string = Soho.Locale.translate('Search');
  public clearTitle: string = Soho.Locale.translate('Clear');
  public searchButtonId: string;
  public clearButtonId: string;
  public formContentHeight: string | null;
  private searchFormBindIds: Array<string>;
  private ignoredBindIds: Array<string> = [];
  private instanceId: string;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private lmBrowserUtilService: LMBrowserUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmCommonUtilService: LMCommonUtilService,
    private lmLoggerService: LMLoggerService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmContextService: LMContextService,
    private ngZone: NgZone
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
  }

  ngOnInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()', 'this.model.hasError=', this.model.hasError ]);

    // todo ppatton may need list id prefixes as well.
    this.searchButtonId = this.formSpec.formElementId + '_Search_button';
    this.clearButtonId = this.formSpec.formElementId + '_Clear_button';

    // IE11 KLUDGE
    if (this.lmBrowserUtilService.isIE11) {
      this.formContentHeight = null;
    } else {
      this.formContentHeight = '100%';
    }

    // create an array of all the bindIds on the search form so it knows which dataView fields to use for the query
    this.searchFormBindIds = this.lmCommonUtilService.getBindIdsFromLayout(this.layout, this.specFields);

    // Need to ignore the member fields of an array field when it's displayed as a lookup
    // TODO refactor LMModelField to add a getter for type, so we don't need to grab from the dataview.fields
    const dataView = this.model.getData();
    this.searchFormBindIds.forEach((bindId: string) => {
      // group - implements compact format and is a group field
      // array - implements compact format (e.g. Races)
      if ((dataView.fields[bindId]?.type === 'group' || dataView.fields[bindId]?.type === 'array') && this.specFields[bindId]?.selectable) {
        this.ignoredBindIds = [ ... this.lmCommonUtilService.getBindIdsFromLayout(this.specFields[bindId].layout, this.specFields), ... this.ignoredBindIds ];
      }
    });
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()', 'this.model.hasError=', this.model.hasError ]);
    this.setMaxHeight();
  }

  public get isCodeBlockFilter(): boolean {
    return this.lmFormSpecUtilService.isCodeBlockFilter(this.formSpec);
  }

  onSearchClicked(event) {
    const filterCriteria = this.getFilterCriteria();

    // null indicates that the search is missing a required parameter
    if (filterCriteria !== null) {
      const filterEvent: LMFilterEvent = {
        event,
        filterCriteria
      };

      this.lmLoggerService.log([ this, this.instanceId, 'onSearchClicked()', filterEvent ]);
      this.searchButtonClicked.emit(filterEvent);
    }
  }

  onClearSearchClicked(event) {
    const filterEvent: LMFilterEvent = {
      event,
      filterCriteria: null
    };

    this.lmLoggerService.log([ this, this.instanceId, 'onClearSearchClicked()', filterEvent ]);
    this.clearSearchButtonClicked.emit(filterEvent);
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  private setMaxHeight() {
    if (this.formOptions && this.formOptions.disableMaxHeightSearchFormKludge) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      if (!this.lmFormContentForm || !this.lmFormContentForm.elementRef) {
        return; // safety
      }

      const nativeElement = this.lmFormContentForm.elementRef.nativeElement;
      let hasToolbar;

      if (nativeElement.closest('.scrollable-flex-header')) {
        hasToolbar = nativeElement.closest('.scrollable-flex-header').getElementsByTagName('lm-toolbar').length !== 0;
      }

      const isSinglePane = nativeElement.closest('.pane.single') !== undefined;

      // extra 40 for search and clear buttons
      const topOffset = nativeElement.getBoundingClientRect().top;
      const bottomOffset = isSinglePane ? 0 : nativeElement.getBoundingClientRect().bottom;
      const buttonsOffset = (hasToolbar ? 40 : 40 * 2);
      const windowHeight = window.innerHeight;
      const totalOffset = topOffset + bottomOffset + buttonsOffset;

      // set maxHeight to about 50%
      nativeElement.style.maxHeight = `${(windowHeight - totalOffset) / 2}px`;
    });
  }

  private computeMultiselectValue(dvField: LMField, dataView: LMDataView): Array<string> {
    const value = [];

    if (dvField.members && Array.isArray(dvField.members)) {
      dvField.members.forEach( key => {
        if (dataView.fields[ key ]) {
          // todo any reason to use lodash trim instead of String.trim? - Phillip 3/23/23
          // const dvFieldValue = dataView.fields[ key ].value.toString().trim();
          const dvFieldValue = trim(dataView.fields[ key ].value.toString());

          if (dvFieldValue.length > 0) {
            value.push(dvFieldValue);
          }
        }
      });

      return value;
    } else {
      // multiselect is a keyfield, not an array field.
      if (Array.isArray(dvField.value)) {
        dvField.value.forEach(key => {
          if (key.length > 0) {
            value.push(key);
          }
        });
      }

      return value;
    }
  }

  // private methods
  private isSearchCriteriaBlank(dvField: LMField, specField: LMSpecField, dataView: LMDataView): boolean {
    if (dvField.members && specField.isMultiselect) {
      const value = this.computeMultiselectValue(dvField, dataView);
      return (value.length === 0);

      // State fields are special cased
    } else if (specField.stateValues && Array.isArray(dvField.value) && (dvField.value as string[]).length > 0) {
      return false;

    } else if (specField.stateValues && dvField.value === 'undefined') {
      return true;
    }
    return this.lmFieldUtilService.isBlank(specField.type, dvField.value);
  }

  // How the different fields should work:
  //     Compact format fields:
  //       build parent field and member fields in the returned array, but filter out the parent compact format when actually calling back to the server
  //     Multiselect fields:
  //       combine all the member fields' values into one string, send that as the value for the parent multiselect field. Need to rebuild the member fields values when navigating back to the list
  //     Basic fields:
  //       send the value in, and the operator chosen
  //         if empty/not empty is chosen, allow blank search value
  public getFilterCriteria(): Array<LMFilterFieldCriteria> {
    const filterFieldArray: Array<LMFilterFieldCriteria> = [];
    const dataView: LMDataView = this.model.getData();
    const dvFields: LMFields = dataView.fields;

    for (let index = 0, len = this.searchFormBindIds.length; index < len; index++) {
      const bindId: string = this.searchFormBindIds [ index ];

      // if we are one of the ignored ids, just continue
      if (this.ignoredBindIds.find((ignoreId: string) => ignoreId === bindId)) {
        continue;
      }

      const dvField: LMField = dvFields[ bindId ];
      const specField: LMSpecField = this.specFields[ bindId ];
      const modelField = this.model.getField(bindId);

      if (modelField === null || dvField === undefined || specField.generated) {
        continue;
      }

      // allow the field to be empty if the field is numeric, bcd or percent
      const allowZero = specField.type === AppConstants.BCD ||
                        specField.type === AppConstants.PERCENT ||
                        specField.type === AppConstants.NUMERIC;

      // allow the field to be empty if the filter type is isEmpty or isNotEmpty
      let allowEmptySearch = allowZero ? true : modelField.filterType ? modelField.filterType === 'isEmpty' || modelField.filterType === 'isNotEmpty' : false;
      allowEmptySearch = allowZero && modelField.required && !this.isCodeBlockFilter ? false : allowEmptySearch;

      if (this.isSearchCriteriaBlank(dvField, specField, dataView) && !allowEmptySearch) {
        // ignore with code block filtering since it is not a true search form
        let addFilterCriteria = false;
        if (modelField.required && !this.isCodeBlockFilter) {
          // allow a zero value for a numeric required search field
          if (allowZero && String(dvField.value)?.length > 0) {
            addFilterCriteria = true;
          } else {
            this.model.setFieldError(bindId, Soho.Locale.translate('RequiredParameter'));
            return null;  // this instructs the caller that the search is canceled
          }
        }
        if (!addFilterCriteria) {
          continue;
        }
      } else if (!(modelField.filterType === 'isEmpty' || modelField.filterType === 'isNotEmpty') &&
          (dvField.value === '' || (Array.isArray(dvField.value) && dvField.value.length === 0))) {
        // don't allow blank value if filter type is not isEmpty or isNotEmpty
        continue;
      }

      const allowMultiSelections = this.lmContextService.useMultiSelectKeyField() &&
        this.model.isSearchForm &&
        !this.model.isCubeSearchForm &&
        !specField.isMultiselect &&
        !specField.isBusClassContextField &&
        // Don't allow multi value entry for Time, Timestamp, Date, Boolean and State Fields fields
        !(specField.type === AppConstants.TIME ||
        specField.type === AppConstants.TIMESTAMP ||
        specField.type === AppConstants.YYYY ||
        specField.type === AppConstants.YYYYMM ||
        specField.type === AppConstants.YYYYMMDD ||
        specField.type === AppConstants.BOOLEAN ||
        specField.type === AppConstants.BINARY ||
        specField.type === AppConstants.BINARYOBJECT ||
        specField.type === AppConstants.BINARYDOCUMENT);

      // issue here as transform options for directives are not available for date/time types
      // if needed, would need to get the information from the layout
      const transformOptions: LMTransformOptions = {
        precision: modelField.precision,
        decSize: specField.decSize,
        allowMultiSelections
      };

      let displayValue = '';
      let dataValue;
      let multiFieldValue: LMMultipleFilterFieldValue;

      if (transformOptions.allowMultiSelections) {

        multiFieldValue = { formattedFilterValue: modelField.value, enteredFilterValue: modelField.enteredFilterValue };

        // In the case that we have opened a record and navigated back to the search, and then refined the search and not
        // changed the value, we were losing the enteredFilterValue. This is because on load we apply the filter criteria
        // to the searchFormDataView in lm-list, and this only handles dvField.value that gets set into modelField.value.
        // Currently there is no way to rehydrate the enteredFilterValue without adding it to the dataview field.
        // This is for https://jira.lawson.com/browse/LMCLIENT-39177
        if (!multiFieldValue.enteredFilterValue) {
          multiFieldValue.enteredFilterValue = multiFieldValue.formattedFilterValue;
        }

        displayValue = multiFieldValue.enteredFilterValue;

        // format the display value for non alpha fields
        if (specField.type !== AppConstants.ALPHA ||
            specField.type !== AppConstants.ALPHALOWER ||
            specField.type !== AppConstants.ALPHARIGHT ||
            specField.type !== AppConstants.ALPHAUPPER ||
            specField.type !== AppConstants.TEXT ||
            specField.type !== AppConstants.TEXTAREA) {
          displayValue = this.lmFormatUtilService.toDisplayValue(modelField.value, specField.type, transformOptions);
        }
      } else {
        displayValue = (this.model.isCubeSearchForm) ? modelField.value : this.lmFormatUtilService.toDisplayValue(modelField.value, specField.type, transformOptions);
      }

      if (specField.isMultiselect) {
        dataValue = this.computeMultiselectValue(dvField, dataView);
        if (dataValue.length === 0) {
          continue;
        }

      } else if (Array.isArray(dvField.value)) {
        dataValue = dvField.value;

        if (transformOptions.allowMultiSelections && multiFieldValue !== undefined) {
          dataValue = multiFieldValue.formattedFilterValue;
        }

      } else if (transformOptions.allowMultiSelections) {
        // need to check to see if the value string has a , and make it into an array if it does
        if (multiFieldValue !== undefined/*(dvField.value as string).indexOf(',')*/) {
          dataValue = multiFieldValue.formattedFilterValue;
        } else {
          dataValue = dvField.value;
        }
      } else {
        dataValue = dvField.value.toString();

        if (dataValue && modelField.filterType !== 'inRange' && dataValue.indexOf(' - ') >= 0 &&
        [ AppConstants.TIMESTAMP, AppConstants.YYYYMMDD ].indexOf(specField.type) !== -1) {
          const range = dataValue.split(' - ');
          dataValue = range[0].trim();
        }
      }
      let operator: LMFilterFieldOperator;

      if (specField.stateValues
        || specField.hasUserDefinedStates
        || dvField.hasShowAsDropdownStates
        || specField.type === AppConstants.BOOLEAN) {
        // field is a state field, so get the state label for the value, i.e. show 'Active' instead of '1'
        displayValue = this.lmFormatUtilService.stateLabelToText(modelField.states, dataValue);
      }

      if (this.model.isCubeSearchForm) {
        operator = 'equals';
      } else if (specField.isBusClassContextField) {
        operator = undefined; // no operator is used when it's a busClassContextField

      } else {
        if (modelField.filterType) {
          operator = modelField.filterType;
        } else if (specField.isMultiselect || specField.stateValues || specField.hasUserDefinedStates) {
          // filter type should be equals for any state field. This is so the searching on $ won't return records with $$
          operator = 'equals';

        } else if (specField.type === AppConstants.BOOLEAN // TODO: need support for other field types
        || specField.type === AppConstants.NUMERIC
        || specField.type === AppConstants.BCD
        || specField.type === AppConstants.PERCENT
        || specField.type === AppConstants.YYYYMMDD
        || specField.type === AppConstants.TIMESTAMP
        || specField.type === AppConstants.YYYYMM
        || specField.type === AppConstants.MMDD
        || specField.type === AppConstants.YYYY) {
          operator = 'equals';

        } else {
          operator = 'contains';
        }
      }

      const label: string = this.lmFormatUtilService.formatMessageRule(specField.label, this.model);

      const filterField: LMFilterFieldCriteria = {
        operator,
        fieldId:                bindId,
        value:                  dataValue,
        label,
        displayValue,
        isCompactFormatField:   specField.compactFormat,
        isBusClassContextField: specField.isBusClassContextField,
        isMultiselectField:     specField.isMultiselect,
        stateValues:            (specField.isMultiselect) ? modelField.states : undefined,
        isRequired:             modelField.required,
        exportFilterCriteria:   modelField.exportFilterCriteria,
        disableTranslationFiltering: modelField.disableTranslationFiltering
      };

      if (this.isCodeBlockFilter) {
        filterField.isCompactFormatField = true;
        filterField.compactFormatColumnId = this.formSpec.busFormSpec.codeBlockFieldId;
      }

      if (specField.displayOnly) {
        filterField.isDisplayOnly = true;
      }

      filterFieldArray.push(filterField);
    }

    this.lmLoggerService.log([ this, this.instanceId, 'getFilterCriteria()', filterFieldArray ]);
    return filterFieldArray;
  }
}
